import React, { useState, useEffect } from 'react';
import Classes from './MenteeViewProfile.module.css';
import {
    FaArrowLeft,
    FaBriefcase,
    FaBuilding,
    FaGraduationCap,
    FaHandsHelping,
    FaLanguage,
    FaCalendarAlt,
    FaRegFileAlt,
    FaGlobe,
    FaLinkedinIn,
    FaBullseye,
    FaLightbulb,
    FaUsers,
    FaStar,
    FaEnvelope,
    FaUserTag,
} from 'react-icons/fa';
import { FaFileLines } from "react-icons/fa6";
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../../../../../utils/Icons';
import backend from '../../../../../api';

export default function ViewProfile() {
    const navigateTo = useNavigate();
    const { id } = useParams();
    const [mentee, setMentee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    async function getMentee() {
        try {
            const resp = await backend.get(`/api/user/${id}`);
            setMentee(resp.data.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching mentee:", err);
            setError(err.response?.data?.error || "Failed to fetch mentee data");
            setLoading(false);
        }
    }

    useEffect(() => {
        getMentee();
    }, [id]);

    if (loading) {
        return <div className={Classes.hero}>Loading...</div>;
    }

    // if (error) {
    //     return <div className={Classes.hero}>Error: {error}</div>;
    // }

    if (!mentee) {
        return <div className={Classes.hero}>Mentee not found</div>;
    }

    const {
        email,
        role,
        menteeProfile: {
            basicInfo: { name, title, education, clubs, bio } = {},
            goalsAndInterests: { goals, lookingFor, interest } = {},
            preferences: { mentorLevel, languagesSpoken, availability } = {},
            extras: { linkedinUrl, portfolioUrl, resume } = {},
        } = {},
    } = mentee;

    return (
        <div className={Classes.hero}>
            {/* Top bar */}
            <div className={Classes.header}>
                <button className={Classes.backBtn} onClick={() => { navigateTo(-1) }} aria-label="Back">
                    <ArrowLeftIcon height="20px" fill="#888787" cursor="pointer" />
                </button>
                <h3>Profile</h3>
            </div>

            {/* Main 2-column layout */}
            <div className={Classes.profileContainer}>
                {/* LEFT CARD */}
                <aside className={Classes.leftPanel}>
                    <div className={Classes.photo} >
                        <img
                            src={mentee?.menteeProfile?.basicInfo?.avatar || '/assets/images/user.png'}
                            alt={name || 'Profile photo'}
                            loading="lazy"
                            decoding="async"
                            width={1600} height={2000}
                            draggable={false}
                        />
                    </div>
                    <div className={Classes.flexAlign7} >
                        <h3 className={Classes.name}>{name || "Unknown Mentee"}</h3>
                    </div>

                    <ul className={Classes.infoList}>
                        <li className={Classes.infoItem}>
                            <FaBriefcase className={Classes.icon} />
                            <div className={Classes.textBlock}>
                                <span className={Classes.label}>Position</span>
                                <span className={Classes.value}>{title || "None"}</span>
                            </div>
                        </li>

                        <li className={Classes.infoItem}>
                            <FaBuilding className={Classes.icon} />
                            <div className={Classes.textBlock}>
                                <span className={Classes.label}>Company</span>
                                <span className={Classes.value}>None</span>
                            </div>
                        </li>

                        <li className={Classes.infoItem}>
                            <FaGraduationCap className={Classes.icon} />
                            <div className={Classes.textBlock}>
                                <span><h5 className={Classes.label}>Education</h5></span>
                                <span><h5 className={Classes.value}>{education || "None"}</h5></span>
                            </div>
                        </li>

                        <li className={Classes.infoItem}>
                            <FaHandsHelping className={Classes.icon} />
                            <div className={Classes.textBlock}>
                                <span className={Classes.label}>Clubs/Charities</span>
                                <span className={Classes.value}>{clubs || "None"}</span>
                            </div>
                        </li>

                        <li className={Classes.infoItem}>
                            <FaLanguage className={Classes.icon} />
                            <div className={Classes.textBlock}>
                                <span className={Classes.label}>Languages</span>
                                <span className={Classes.value}>{languagesSpoken?.join(", ") || "None"}</span>
                            </div>
                        </li>

                        <li className={Classes.infoItem}>
                            <FaCalendarAlt className={Classes.icon} />
                            <div className={Classes.textBlock}>
                                <span className={Classes.label}>Availability</span>
                                <span className={Classes.value}>{availability?.join(", ") || "None"}</span>
                            </div>
                        </li>

                        {/* <li className={Classes.infoItem}>
                            <FaEnvelope className={Classes.icon} />
                            <div className={Classes.textBlock}>
                                <span className={Classes.label}>Email</span>
                                <span className={Classes.value}>{email || "None"}</span>
                            </div>
                        </li> */}
                    </ul>

                    <button
                        className={Classes.messageBtn}
                        onClick={() => navigateTo(`/messages?userId=${id}`)}
                    >
                        Message
                    </button>
                </aside>

                {/* RIGHT COLUMN */}
                <div className={Classes.rightPanel}>
                    {/* Personal Bio */}
                    <section className={Classes.section}>
                        <header className={Classes.sectionHeader}>
                            <h3>Personal Bio</h3>
                            <div className={Classes.sectionActions}>
                                {resume && (
                                    <a href={resume} target="_blank" rel="noopener noreferrer">
                                        <FaFileLines />
                                    </a>
                                )}
                                {portfolioUrl && (
                                    <a href={portfolioUrl} target="_blank" rel="noopener noreferrer">
                                        <FaGlobe />
                                    </a>
                                )}
                                {linkedinUrl && (
                                    <a href={linkedinUrl} target="_blank" rel="noopener noreferrer">
                                        <FaLinkedinIn />
                                    </a>
                                )}
                            </div>
                        </header>
                        <p>{bio || "No bio provided."}</p>
                    </section>

                    {/* Professional Experience */}
                    <section className={Classes.section}>
                        <h3 className={Classes.sectionTitle}>Professional Experience</h3>

                        <div className={Classes.subItem}>
                            <FaBullseye className={Classes.icon} />
                            <div>
                                <h4 className={Classes.subItemTitle}>Goals</h4>
                                <p className={Classes.value}>{goals || "None"}</p>
                            </div>
                        </div>

                        <div className={Classes.subItem}>
                            <FaLightbulb className={Classes.icon} />
                            <div>
                                <h4 className={Classes.subItemTitle}>Areas of Interest</h4>
                                <p className={Classes.value}>{interest?.join(", ") || "None"}</p>
                            </div>
                        </div>
                    </section>

                    {/* Other Details */}
                    <section className={Classes.section}>
                        <h3 className={Classes.sectionTitle}>Other Details</h3>

                        <div className={Classes.subItem}>
                            <FaUsers className={Classes.icon} />
                            <div>
                                <h4 className={Classes.subItemTitle}>What They Are Looking For</h4>
                                <p className={Classes.value}>{lookingFor || "None"}</p>
                            </div>
                        </div>

                        <div className={Classes.subItem}>
                            <FaStar className={Classes.icon} />
                            <div>
                                <h4 className={Classes.subItemTitle}>Preferred Mentor Level</h4>
                                <p className={Classes.value}>{mentorLevel?.join(", ") || "None"}</p>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}